import React, { useEffect, useState } from 'react';
import {
  Switch,
  Button,
  Input,
  Checkbox,
  ConfirmationModal,
  TooltipHoverArea,
} from '@puppet/react-components';
import { useListUsersByPrefix, useSetSuperUser } from '@services/cd4pe/users';
import { Table } from '@puppet/data-grid';
import Cd4peError from '@components/Cd4peError';
import { Trans, useTranslation } from 'react-i18next';
import { UserScopeV1, UserV1 } from '@puppet/cd4pe-client-ts';
import { selectUser } from '@state/session/selectors';
import useDebouncedSearch from '@hooks/useDebouncedSearch';
import alerts from '@state/ui/alerts';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { sortTableData } from '@utils/sort';
import { NavLink } from 'react-router-dom';
import DeleteUserModal from './DeleteUserModal';
import { LINKS } from '../../../../routes';

interface Column {
  label: string;
  dataKey?: keyof UserV1;
  sortable?: boolean;
  cellRenderer?: (params: { cellData: UserV1 }) => React.ReactNode;
  cellDataGetter?: (params: { rowData: UserV1 }) => UserV1;
}

const RootAccountsTable = () => {
  const { userName: loggedInUser, domain } = useAppSelector(
    selectUser,
  ) as UserV1;
  const { searchTerm, appliedSearchTerm, handleSearch } = useDebouncedSearch();
  const [listUsersScope, setListUsersScope] = useState<UserScopeV1>('GLOBAL');
  const listUsers = useListUsersByPrefix({
    workspaceId: domain!,
    prefix: appliedSearchTerm,
    scope: listUsersScope,
  });
  const setSuperUser = useSetSuperUser(loggedInUser!);
  const { t } = useTranslation('codeDelivery');
  const appDispatch = useAppDispatch();

  const [tableData, setTableData] = useState<UserV1[] | undefined>([]);
  const [sortedColumn, setSortedColumn] = useState<{
    sortDataKey: keyof UserV1;
    direction: boolean | 'asc' | 'desc';
  }>();
  const [userToDelete, setUserToDelete] = useState<UserV1 | undefined>(
    undefined,
  );
  const [userToConvertToSuperUser, setUserToConvertToSuperUser] = useState<
    string | undefined
  >(undefined);
  const [userToRemoveSuperUser, setUserToRemoveSuperUser] = useState<
    string | undefined
  >(undefined);
  const [scopeCheckboxState, setScopeCheckboxState] = useState(false);

  useEffect(() => {
    setTableData(listUsers.data?.users ?? []);
  }, [listUsers.data]);

  const onChangeIsSuperUser = (username: string, newSuperUserVal: boolean) => {
    setSuperUser.mutate(
      {
        username,
        superUser: newSuperUserVal ? 'true' : 'false',
      },
      {
        onSuccess: () => {
          appDispatch(
            alerts.actions.createAlert({
              type: 'Success',
              message: t(
                `root.accounts.alert.${
                  newSuperUserVal ? 'addToSuperUser' : 'removeFromSuperUser'
                }.success`,
                {
                  username,
                },
              ),
            }),
          );
          setUserToConvertToSuperUser(undefined);
          setUserToRemoveSuperUser(undefined);
        },
      },
    );
  };

  const onSortHandler = (
    newDirection: 'asc' | 'desc',
    newDataKey: keyof UserV1,
  ) => {
    const sortedData = [...tableData!];
    sortTableData(sortedData, newDataKey, newDirection);

    setTableData(sortedData);
    setSortedColumn({
      direction: newDirection,
      sortDataKey: newDataKey,
    });
  };

  const columns: Column[] = [
    {
      label: t('root.accounts.table.columnHeader.username'),
      dataKey: 'userName',
      sortable: true,
    },
    {
      label: t('root.accounts.table.columnHeader.email'),
      dataKey: 'email',
      sortable: true,
    },
    {
      label: t('root.accounts.table.columnHeader.id'),
      dataKey: 'id',
      sortable: true,
    },
    {
      label: t('root.accounts.table.columnHeader.isSuperUser'),
      dataKey: 'superUser',
      sortable: true,
      cellDataGetter: ({ rowData }) => rowData,
      cellRenderer: ({ cellData }) => {
        const { superUser, id, userName } = cellData;
        return (
          <Switch
            key={id}
            data-testid={`isSuperUser-switch-${userName}`}
            name={`isSuperUserSwitch-${id}`}
            checked={superUser}
            onChange={() =>
              (superUser
                ? setUserToRemoveSuperUser
                : setUserToConvertToSuperUser)(userName)
            }
            label={
              superUser
                ? t('root.accounts.table.superUser.switch.true')
                : t('root.accounts.table.superUser.switch.false')
            }
            disabled={id === 1} // ID of root user is always 1, disables superuser toggle for root user
          />
        );
      },
    },
    {
      label: t('root.accounts.table.columnHeader.actions'),
      cellDataGetter: ({ rowData }) => rowData,
      cellRenderer: ({ cellData }) => {
        const user = cellData;
        const isRootUser = user.id === 1;
        const deleteUserToolTip = isRootUser
          ? t('root.accounts.table.columnHeader.actions.deleteUser.disabled')
          : t('root.accounts.table.columnHeader.actions.deleteUser');

        return (
          <div className="accounts-table__content__actions-wrapper">
            <TooltipHoverArea
              tooltip={t(
                'root.accounts.table.columnHeader.actions.userSettings',
              )}
              anchor="bottom"
            >
              <Button
                as={NavLink}
                to={LINKS.root.userAccount({ path: { id: user.id! } })}
                data-testid={`edit-user-button-${user.userName}`}
                icon="gear"
                type="transparent"
              />
            </TooltipHoverArea>
            <TooltipHoverArea tooltip={deleteUserToolTip} anchor="bottom">
              <Button
                onClick={() => setUserToDelete(user)}
                icon="trash"
                type="transparent"
                data-testid={`delete-user-button-${user.userName}`}
                disabled={isRootUser}
              />
            </TooltipHoverArea>
          </div>
        );
      },
    },
  ];

  const onChangeScopeCheckbox = (superUserFilter: boolean) => {
    setListUsersScope(superUserFilter ? 'SUPERUSERS' : 'GLOBAL');
    setScopeCheckboxState(superUserFilter);
  };

  return (
    <div className="accounts-table">
      <div className="accounts-table__filters">
        <Input
          name="accounts-table__search-input"
          placeholder={t('root.accounts.table.search.input.placeholder')}
          value={searchTerm}
          onChange={handleSearch}
          className="search-input"
          icon="search"
          data-testid="root-search-users"
        />
        <Checkbox
          name="scope-checkbox"
          data-testid="scope-checkbox"
          label={t('root.accounts.table.filter.scope.checkbox.label')}
          value={scopeCheckboxState}
          onChange={onChangeScopeCheckbox}
        />
      </div>
      <div className="accounts-table__content">
        <Table
          columns={columns}
          data={tableData}
          onSort={onSortHandler}
          sortedColumn={sortedColumn}
          rowKey="id"
          loading={listUsers.isLoading}
          emptyStateHeader={t('root.accounts.table.noUsersFound')}
          emptyStateMessage=""
          data-testid="root-accounts-table"
        />
      </div>
      <Cd4peError error={listUsers.error} />
      <Cd4peError error={setSuperUser.error} />
      {userToDelete && (
        <DeleteUserModal
          username={userToDelete.userName ?? ''}
          onClose={() => setUserToDelete(undefined)}
        />
      )}
      {userToConvertToSuperUser && (
        <ConfirmationModal
          title={t('root.accounts.modal.addToSuperUser.title')}
          description={
            <Trans
              t={t}
              i18nKey="root.accounts.modal.addToSuperUser.description"
              components={[<b />]}
              values={{ username: userToConvertToSuperUser }}
            />
          }
          onCancel={() => setUserToConvertToSuperUser(undefined)}
          onConfirm={() => onChangeIsSuperUser(userToConvertToSuperUser, true)}
          confirmLabel={t('root.accounts.modal.addToSuperUser.confirm')}
          cancelLabel={t('root.accounts.modal.addToSuperUser.cancel')}
          confirmButtonLoading={setSuperUser.isLoading}
        />
      )}
      {userToRemoveSuperUser && (
        <ConfirmationModal
          title={t('root.accounts.modal.removeFromSuperUser.title')}
          description={
            <Trans
              t={t}
              i18nKey="root.accounts.modal.removeFromSuperUser.description"
              components={[<b />]}
              values={{ username: userToRemoveSuperUser }}
            />
          }
          onConfirm={() => onChangeIsSuperUser(userToRemoveSuperUser, false)}
          onCancel={() => setUserToRemoveSuperUser(undefined)}
          confirmButtonType="danger"
          confirmLabel={t('root.accounts.modal.removeFromSuperUser.confirm')}
          cancelLabel={t('root.accounts.modal.removeFromSuperUser.cancel')}
          confirmButtonLoading={setSuperUser.isLoading}
        />
      )}
    </div>
  );
};

export default RootAccountsTable;
